
import React, { useState, useEffect } from 'react';
import { Package, Calendar, Building, Camera, Edit, Trash2 } from 'lucide-react';
import { X, User, ArrowUpRight, ArrowDownLeft, RefreshCw, Download, MapPin, Upload, Maximize2, Eye } from './icons';
import { consumiblesAPI, actasAPI } from '../services/api';
import { getApiBaseUrl } from '../config';
import { useToast } from '../contexts/ToastContext';
import { formatUbicacion } from '../utils/locationUtils';

export default function DetalleConsumibleModal({ isOpen, onClose, consumible }) {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [movimientos, setMovimientos] = useState([]);
    const [activeTab, setActiveTab] = useState('info'); // 'info' or 'history'
    const [uploadingImage, setUploadingImage] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [showLightbox, setShowLightbox] = useState(false);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        const fetchMovimientos = async () => {
            try {
                setLoading(true);
                const res = await consumiblesAPI.getMovimientos(consumible.id);
                setMovimientos(res.data || []);
            } catch (error) {
                console.error(error);
                showToast('Error al cargar movimientos', 'error');
            } finally {
                setLoading(false);
            }
        };

        if (isOpen && consumible?.id) {
            fetchMovimientos();
        } else {
            setMovimientos([]);
        }
    }, [isOpen, consumible, showToast]);

    useEffect(() => {
        if (consumible?.imagenUrl) {
            // Si la URL es relativa, agregar el host del API
            const baseUrl = getApiBaseUrl();
            const url = consumible.imagenUrl.startsWith('http')
                ? consumible.imagenUrl
                : `${baseUrl}${consumible.imagenUrl}`;
            setCurrentImage(url);
        } else {
            setCurrentImage(null);
        }
    }, [consumible]);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validar tipo
        if (!file.type.startsWith('image/')) {
            showToast('Solo se permiten archivos de imagen', 'error');
            return;
        }

        // Validar tamaño (5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast('La imagen no debe superar los 5MB', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('imagen', file);

        try {
            setUploadingImage(true);
            const res = await consumiblesAPI.uploadImage(consumible.id, formData);
            // Actualizar vista inmediata con URL completa
            const baseUrl = getApiBaseUrl();
            const url = `${baseUrl}${res.data.url}`;
            setCurrentImage(url);
            showToast('Imagen actualizada correctamente', 'success');
        } catch (error) {
            console.error('Error uploading image:', error);
            showToast('Error al subir la imagen', 'error');
        } finally {
            setUploadingImage(false);
        }
    };

    const handlePreviewComprobante = async (movimientoId) => {
        try {
            const response = await actasAPI.previsualizarMovimiento(movimientoId);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error previewing:', error);
            showToast('Error al previsualizar comprobante', 'error');
        }
    };

    const handleDeleteMovimiento = async (movimientoId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este movimiento? Se restaurará el stock correspondiente.')) return;

        try {
            await consumiblesAPI.deleteMovimiento(movimientoId);
            showToast('Movimiento eliminado correctamente', 'success');
            // Recargar movimientos
            const res = await consumiblesAPI.getMovimientos(consumible.id);
            setMovimientos(res.data || []);
        } catch (error) {
            console.error('Error deleting movement:', error);
            showToast('Error al eliminar el movimiento', 'error');
        }
    };

    const handleDeleteImage = async () => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar la imagen?')) return;

        try {
            await consumiblesAPI.deleteImage(consumible.id);
            setCurrentImage(null);
            setShowLightbox(false);
            showToast('Imagen eliminada correctamente', 'success');
        } catch (error) {
            console.error('Error deleting image:', error);
            showToast('Error al eliminar la imagen', 'error');
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };



    if (!isOpen || !consumible) return null;

    // Lightbox Component
    const Lightbox = () => {
        if (!showLightbox || !currentImage) return null;
        return (
            <div className="fixed inset-0 z-[10000] bg-black bg-opacity-90 flex flex-col items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
                <button
                    onClick={() => setShowLightbox(false)}
                    className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
                >
                    <X className="w-8 h-8" />
                </button>

                <img
                    src={currentImage}
                    alt="Vista completa"
                    className="max-h-[80vh] max-w-full object-contain mb-6 rounded-lg shadow-2xl"
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
                />

                <div className="flex space-x-4" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={() => {
                            triggerFileInput();
                            // No cerramos el lightbox inmediatamente, esperamos a que el usuario seleccione.
                            // Pero el input file está en el componente padre. 
                            // Al seleccionar, se sube y actualiza currentImage... 
                            // pero quizás sea mejor cerrar el lightbox o mostrar loading en él.
                            // Por simplicidad, dejemos abierto y el loading se muestra en el padre? No, el lightbox tapa.
                            // Debemos cerrar el lightbox al abrir el file picker o mostrar status.
                            // Cerremos lightbox para ver el proceso de carga en thumbnail.
                            setShowLightbox(false);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Edit className="w-5 h-5" />
                        <span>Cambiar Imagen</span>
                    </button>
                    <button
                        onClick={handleDeleteImage}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        <Trash2 className="w-5 h-5" />
                        <span>Eliminar</span>
                    </button>
                </div>
            </div>
        );
    };

    const getMovimientoIcon = (tipo) => {
        switch (tipo) {
            case 'ENTRADA': return <ArrowDownLeft className="w-5 h-5 text-green-500" />;
            case 'SALIDA': return <ArrowUpRight className="w-5 h-5 text-blue-500" />;
            case 'AJUSTE': return <RefreshCw className="w-5 h-5 text-orange-500" />;
            default: return <Package className="w-5 h-5 text-gray-500" />;
        }
    };

    const getMovimientoColor = (tipo) => {
        switch (tipo) {
            case 'ENTRADA': return 'bg-green-50 text-green-700 border-green-200';
            case 'SALIDA': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'AJUSTE': return 'bg-orange-50 text-orange-700 border-orange-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-t-lg">
                    <div className="flex items-center space-x-3">
                        <div className="relative group">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/*"
                                className="hidden"
                            />

                            {currentImage ? (
                                <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 cursor-pointer group" onClick={() => setShowLightbox(true)}>
                                    <img
                                        src={currentImage}
                                        alt={consumible.nombre}
                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                        onError={(e) => {
                                            setCurrentImage(null);
                                        }}
                                    />
                                    {/* Overlay con icono de lupa/maximizar */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                        <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                    </div>
                                    {uploadingImage && (
                                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div
                                    onClick={triggerFileInput}
                                    className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors relative"
                                    title="Subir imagen"
                                >
                                    {uploadingImage ? (
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                                    ) : (
                                        <Camera className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                    )}
                                    <div className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-800 rounded-full p-0.5 border border-gray-200 dark:border-gray-600">
                                        <Upload className="w-3 h-3 text-gray-500" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {consumible.nombre}
                            </h2>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {consumible.categoria}
                            </span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('info')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'info'
                            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-50'
                            }`}
                    >
                        Información General
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'history'
                            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-50'
                            }`}
                    >
                        Historial de Movimientos
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {activeTab === 'info' ? (
                        <div className="space-y-6">
                            {/* Stock Display */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Stock Actual</h3>
                                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                        {consumible.stockActual}
                                        <span className="text-sm font-normal text-gray-400 ml-2">unidades</span>
                                    </div>
                                    <div className={`mt-2 text-xs font-medium px-2 py-1 rounded inline-block ${consumible.stockActual <= consumible.stockMinimo
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-green-100 text-green-700'
                                        }`}>
                                        {consumible.stockActual <= consumible.stockMinimo ? 'Stock Bajo' : 'Stock Saludable'}
                                    </div>
                                </div>
                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Stock Mínimo</h3>
                                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                        {consumible.stockMinimo}
                                        <span className="text-sm font-normal text-gray-400 ml-2">unidades</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">Punto de reorden</p>
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Ubicación</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">{formatUbicacion(consumible.ubicacion) || 'Sin asignar'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Valor unitario</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">{consumible.valor != null ? `$${Number(consumible.valor).toLocaleString('es-CL')}` : 'Sin valor'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Empresa</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">{consumible.empresa || 'Sin asignar'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="mt-0.5"><Package className="w-5 h-5 text-gray-400" /></div>
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Descripción</h4>
                                            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                                                {consumible.descripcion || 'Sin descripción'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {loading ? (
                                <div className="text-center py-8 text-gray-500">Cargando historial...</div>
                            ) : movimientos.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">No hay movimientos registrados</div>
                            ) : (
                                <div className="space-y-3">
                                    {movimientos.map((mov) => (
                                        <div key={mov.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-lg hover:shadow-sm transition-shadow">
                                            <div className="flex items-center space-x-3">
                                                <div className={`p-2 rounded-full ${getMovimientoColor(mov.tipo)} bg-opacity-20`}>
                                                    {getMovimientoIcon(mov.tipo)}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {mov.tipo} - {mov.cantidad} unid.
                                                    </p>
                                                    <div className="text-xs text-gray-500 flex items-center space-x-2">
                                                        <span className="flex items-center">
                                                            <Calendar className="w-3 h-3 mr-1" />
                                                            {new Date(mov.fecha).toLocaleDateString()} {new Date(mov.fecha).toLocaleTimeString()}
                                                        </span>
                                                        {mov.usuario && (
                                                            <span className="flex items-center text-blue-600 dark:text-blue-400">
                                                                <User className="w-3 h-3 mr-1" />
                                                                {mov.usuario.nombre} {mov.usuario.apellido}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {mov.tipo === 'SALIDA' && (
                                                <div className="flex items-center space-x-1">
                                                    <button
                                                        onClick={() => handlePreviewComprobante(mov.id)}
                                                        className="text-gray-400 hover:text-blue-600 p-2"
                                                        title="Previsualizar Comprobante"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMovimiento(mov.id)}
                                                        className="text-gray-400 hover:text-red-600 p-2"
                                                        title="Eliminar Movimiento"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                            {mov.tipo !== 'SALIDA' && (
                                                <button
                                                    onClick={() => handleDeleteMovimiento(mov.id)}
                                                    className="text-gray-400 hover:text-red-600 p-2"
                                                    title="Eliminar Movimiento"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>

            <Lightbox />
        </div>
    );
}
